import { useState, useMemo } from 'react'
import { useDepartment } from '../context/DepartmentContext'
import { useToast } from '../context/ToastContext'
import { getFlags, resolveFlag, resolveFlagsBulk, deduplicateFlags } from '../lib/store'
import { getSeverityColor, getSeverityBadge, FLAG_CODES } from '../lib/flagging'
import { formatDate } from '../lib/calculations'
import { Filter, CheckSquare, BarChart3 } from 'lucide-react'
import DateInput from '../components/ui/DateInput'
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer
} from 'recharts'

export default function Flags() {
  const { currentDepartmentId } = useDepartment()
  const { addToast } = useToast()
  const today = new Date().toISOString().split('T')[0]
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().split('T')[0]

  const [startDate, setStartDate] = useState(thirtyDaysAgo)
  const [endDate, setEndDate] = useState(today)
  const [severity, setSeverity] = useState('')
  const [flagCode, setFlagCode] = useState('')
  const [showResolved, setShowResolved] = useState(false)
  const [selected, setSelected] = useState(new Set())

  const flags = useMemo(() => {
    let result = getFlags(currentDepartmentId, { startDate, endDate })
    if (severity) result = result.filter(f => f.severity === severity)
    if (flagCode) result = result.filter(f => f.flagCode === flagCode)
    if (!showResolved) result = result.filter(f => !f.resolved)
    return result.sort((a, b) => {
      const sev = { HIGH: 0, MEDIUM: 1, LOW: 2 }
      return (sev[a.severity] || 3) - (sev[b.severity] || 3) || b.date?.localeCompare(a.date)
    })
  }, [currentDepartmentId, startDate, endDate, severity, flagCode, showResolved])

  // Flag frequency
  const frequency = useMemo(() => {
    const map = {}
    for (const f of flags) {
      map[f.flagCode] = (map[f.flagCode] || 0) + 1
    }
    return Object.entries(map)
      .map(([code, count]) => ({ code, count }))
      .sort((a, b) => b.count - a.count)
  }, [flags])

  // Daily flag count trend
  const dailyTrend = useMemo(() => {
    const map = {}
    for (const f of flags) {
      if (!f.date) continue
      map[f.date] = (map[f.date] || 0) + 1
    }
    return Object.entries(map)
      .map(([date, count]) => ({ date: formatDate(date), count }))
      .sort((a, b) => a.date.localeCompare(b.date))
  }, [flags])

  const handleResolve = (id) => {
    resolveFlag(id)
    setSelected(prev => { const s = new Set(prev); s.delete(id); return s })
    addToast('Flag resolved', 'success')
  }

  const handleBulkResolve = () => {
    if (selected.size === 0) return
    resolveFlagsBulk([...selected])
    setSelected(new Set())
    addToast(`Resolved ${selected.size} flags`, 'success')
  }

  const handleDedup = () => {
    const removed = deduplicateFlags(currentDepartmentId)
    addToast(removed > 0 ? `Removed ${removed} duplicate flags` : 'No duplicates found', removed > 0 ? 'success' : 'info')
  }

  const toggleSelect = (id) => {
    setSelected(prev => {
      const s = new Set(prev)
      if (s.has(id)) s.delete(id)
      else s.add(id)
      return s
    })
  }

  const uniqueCodes = [...new Set(flags.map(f => f.flagCode))]

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="text-xl font-bold">Flag Management Centre</h1>
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">{flags.length} flags</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-gray-500" />
          <DateInput value={startDate} onChange={setStartDate} />
          <span className="text-gray-500 text-sm">to</span>
          <DateInput value={endDate} onChange={setEndDate} />
        </div>
        <select value={severity} onChange={e => setSeverity(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
          <option value="">All Severity</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
        <select value={flagCode} onChange={e => setFlagCode(e.target.value)}
          className="bg-gray-800 border border-gray-700 rounded px-2 py-1.5 text-sm text-white">
          <option value="">All Codes</option>
          {uniqueCodes.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-400">
          <input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)}
            className="rounded bg-gray-800 border-gray-700" />
          Show resolved
        </label>
        {selected.size > 0 && (
          <button
            onClick={handleBulkResolve}
            className="flex items-center gap-1 bg-green-800 hover:bg-green-700 text-white px-3 py-1.5 rounded text-sm"
          >
            <CheckSquare size={14} /> Resolve {selected.size}
          </button>
        )}
        <button
          onClick={handleDedup}
          className="flex items-center gap-1 bg-gray-800 hover:bg-gray-700 text-gray-300 px-3 py-1.5 rounded text-sm"
        >
          <Filter size={14} /> Clean Duplicates
        </button>
      </div>

      {/* Flag Frequency */}
      {frequency.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3 flex items-center gap-2"><BarChart3 size={14} /> Flag Frequency</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {frequency.map(f => (
              <div key={f.code} className="bg-gray-800/50 rounded px-3 py-2 flex justify-between">
                <span className="text-xs font-mono">{f.code}</span>
                <span className="text-xs font-bold">{f.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Daily Trend */}
      {dailyTrend.length > 0 && (
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <h3 className="text-sm font-medium mb-3">Daily Flag Count</h3>
          <ResponsiveContainer width="100%" height={120}>
            <BarChart data={dailyTrend}>
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#6b7280' }} />
              <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} />
              <Tooltip contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }} />
              <Bar dataKey="count" fill="#f59e0b" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Flag List */}
      <div className="space-y-2">
        {flags.map(f => (
          <div key={f.id} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${
            f.resolved ? 'bg-gray-900/50 border-gray-800 opacity-60' : getSeverityColor(f.severity)
          }`}>
            {!f.resolved && (
              <input
                type="checkbox"
                checked={selected.has(f.id)}
                onChange={() => toggleSelect(f.id)}
                className="rounded bg-gray-800 border-gray-700"
              />
            )}
            <span className={`text-xs px-1.5 py-0.5 rounded ${getSeverityBadge(f.severity)}`}>{f.severity}</span>
            <span className="text-xs font-mono w-48">{f.flagCode}</span>
            <span className="text-xs text-gray-400 w-24">{f.date ? formatDate(f.date) : '—'}</span>
            <span className="text-xs flex-1">{f.description}</span>
            {f.resolved ? (
              <span className="text-xs text-green-600">Resolved</span>
            ) : (
              <button
                onClick={() => handleResolve(f.id)}
                className="text-xs text-gray-400 hover:text-white px-2 py-1 rounded bg-gray-800"
              >
                Resolve
              </button>
            )}
          </div>
        ))}
        {flags.length === 0 && (
          <div className="text-center py-12 text-gray-600 text-sm">No flags found for this period</div>
        )}
      </div>
    </div>
  )
}
